/**
 * AddToDeckMenu - Dropdown menu for adding cards to decks
 *
 * Shows a list of user's decks with copy count indicators.
 * Uses useDeckAddability to check format-appropriate copy limits.
 * Supports quick-add to main deck or add-with-options for zone selection.
 */
import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GiCardPlay } from 'react-icons/gi';
import { IoCheckmark, IoEllipsisHorizontal, IoLockClosed } from 'react-icons/io5';

import { useAddDeckCardMutation, useDecksQuery, useUpdateDeckCardMutation } from '@/features/decks/hooks/api';
import { allDeckCardsAtom } from '@/features/decks/state/allDeckCards';
import { useDeckAddability } from '@/hooks/useCollectionAvailability';
import { DECK_STATUS_LABELS } from '@/lib/deckUtils';
import { canMoveToZone, getDefaultZoneForCard } from '@/lib/deckValidation';
import type { ScryfallCard } from '@/types/card';
import type { Deck, DeckZone } from '@/types/deck';

// ============================================================================
// Types
// ============================================================================

export interface AddToDeckMenuProps {
  /** The card to add (needs name for copy limit checking, scryfall_id for adding) */
  card: ScryfallCard | null;
  /** Button variant - icon only or with text */
  variant?: 'icon' | 'button';
  /** Custom class for the trigger button */
  className?: string;
}

interface DeckRowProps {
  deck: Deck;
  card: ScryfallCard;
  onAdd: (deckId: string, zone: DeckZone) => Promise<void>;
  isLoading: boolean;
}

// ============================================================================
// Sub-components
// ============================================================================

function DeckRow({ deck, card, onAdd, isLoading }: DeckRowProps): ReactElement {
  const addability = useDeckAddability(card.name, card, deck.id, deck.format);
  const [showZoneSelect, setShowZoneSelect] = useState(false);

  const zoneOptions = useMemo(
    () => (['main', 'sideboard', 'maybeboard'] as const).filter((zone) => canMoveToZone(card, zone, deck.format, deck.status).allowed),
    [card, deck.format, deck.status],
  );

  const handleQuickAdd = useCallback(() => {
    if (addability.canAdd && !deck.is_locked) {
      onAdd(deck.id, 'main');
    }
  }, [addability.canAdd, deck.id, deck.is_locked, onAdd]);

  const handleZoneAdd = useCallback(
    (zone: DeckZone) => {
      onAdd(deck.id, zone);
      setShowZoneSelect(false);
    },
    [deck.id, onAdd],
  );

  const copyLabel = addability.maxCopies >= 99 ? `${addability.inCurrentDeck}` : `${addability.inCurrentDeck}/${addability.maxCopies}`;

  // Status-based styling
  const statusDot = deck.status === 'active' ? 'bg-emerald-500' : deck.status === 'brewing' ? 'bg-amber-500' : 'bg-slate';

  const isDisabled = isLoading || addability.isAtCopyLimit || deck.is_locked;

  return (
    <div className="group relative">
      <button
        onClick={handleQuickAdd}
        disabled={isDisabled}
        className={clsx(
          'flex w-full items-center gap-3 py-2 pl-4 text-left text-sm transition-colors',
          // Reserve right padding when the hover zone-add button can appear, so it doesn't overlay the copy badge
          !isDisabled ? 'pr-10' : 'pr-4',
          isDisabled ? 'text-ash cursor-not-allowed' : 'text-silver hover:bg-slate hover:text-pearl',
        )}
      >
        {/* Status dot */}
        <span className={clsx('h-2 w-2 shrink-0 rounded-full', statusDot)} title={DECK_STATUS_LABELS[deck.status]} />

        {/* Deck name */}
        <span className="min-w-0 flex-1 truncate">{deck.name}</span>

        {/* Copy count indicator */}
        <span
          className={clsx(
            'shrink-0 rounded px-1.5 py-0.5 text-xs font-medium',
            addability.inCurrentDeck > 0 ? 'bg-aether/20 text-aether' : 'bg-slate text-ash',
            addability.isAtCopyLimit && 'bg-emerald-500/20 text-emerald-400',
          )}
        >
          {addability.isAtCopyLimit ? <IoCheckmark className="h-3 w-3" /> : copyLabel}
        </span>

        {/* Lock indicator */}
        {deck.is_locked && <IoLockClosed className="text-ash h-3.5 w-3.5 shrink-0" title="Deck is locked" />}
      </button>

      {/* Zone selector dropdown (shown on hover when deck isn't at limit) */}
      {!isDisabled && zoneOptions.length > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowZoneSelect(!showZoneSelect);
          }}
          className="text-ash hover:text-aether hover:bg-slate/50 absolute top-1/2 right-3 -translate-y-1/2 rounded p-1 text-xs opacity-0 transition-opacity group-hover:opacity-100"
          title="More options (choose zone)"
          aria-label="More options (choose zone)"
        >
          <IoEllipsisHorizontal className="h-3.5 w-3.5" />
        </button>
      )}

      {/* Zone selector submenu */}
      {showZoneSelect && (
        <div className="bg-obsidian border-storm absolute top-full left-8 z-10 rounded border py-1 shadow-lg">
          {zoneOptions.map((zone) => (
            <button
              key={zone}
              onClick={() => handleZoneAdd(zone)}
              className="text-silver hover:bg-slate hover:text-pearl w-full px-4 py-1.5 text-left text-xs capitalize transition-colors"
            >
              {zone === 'main' ? 'Main Deck' : zone === 'sideboard' ? 'Sideboard' : 'Maybeboard'}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function AddToDeckMenu({ card, variant = 'icon', className }: AddToDeckMenuProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Data hooks
  const { data: decks } = useDecksQuery();
  const allDeckCards = useAtomValue(allDeckCardsAtom);
  const addCardMutation = useAddDeckCardMutation();
  const updateCardMutation = useUpdateDeckCardMutation();

  // Non-archived decks, sorted by status (active first) then by name
  const availableDecks = useMemo(() => {
    if (!decks) return [];
    return decks
      .filter((d) => d.status !== 'archived')
      .sort((a, b) => {
        // Active first, then brewing
        if (a.status === 'active' && b.status !== 'active') return -1;
        if (a.status !== 'active' && b.status === 'active') return 1;
        return a.name.localeCompare(b.name);
      });
  }, [decks]);

  // Deck IDs of non-archived decks (archived decks are no longer formed, so their
  // cards are considered free and shouldn't mark the card as "in a deck")
  const activeDeckIds = useMemo(() => new Set(availableDecks.map((d) => d.id)), [availableDecks]);

  // Check if card is in any non-archived deck
  const isInAnyDeck = useMemo(() => {
    if (!card || !allDeckCards.data) return false;
    const nameLower = card.name.toLowerCase();
    return allDeckCards.data.some(
      (dc) => activeDeckIds.has(dc.deck_id) && (dc.scryfall_id === card.id || dc.card_name?.toLowerCase() === nameLower),
    );
  }, [card, allDeckCards.data, activeDeckIds]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleAddToDeck = useCallback(
    async (deckId: string, zone: DeckZone) => {
      if (!card) return;

      // Override zone to 'tokens' for tokens/emblems
      const effectiveZone = getDefaultZoneForCard(card) === 'tokens' ? 'tokens' : zone;

      // Check if card already exists in the deck (same scryfall_id and zone)
      const existingCard = allDeckCards.data?.find(
        (dc) => dc.deck_id === deckId && dc.scryfall_id === card.id && dc.zone === effectiveZone,
      );

      setIsLoading(true);
      try {
        if (existingCard) {
          // Update quantity of existing card
          await updateCardMutation.mutateAsync({
            deckId,
            cardId: existingCard.id,
            data: { quantity: existingCard.quantity + 1 },
          });
        } else {
          // Add new card
          await addCardMutation.mutateAsync({
            deckId,
            card: {
              scryfall_id: card.id,
              card_name: card.name,
              quantity: 1,
              zone: effectiveZone,
            },
          });
        }
      } catch (error) {
        console.error('Failed to add card to deck:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [card, allDeckCards.data, addCardMutation, updateCardMutation],
  );

  if (!card) return <></>;

  return (
    <div ref={menuRef} className="relative">
      {/* Trigger button */}
      {variant === 'icon' ? (
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={clsx('rounded-lg p-1.5 transition-colors', isInAnyDeck ? 'text-aether' : 'text-ash hover:text-aether', className)}
          aria-expanded={isOpen}
          aria-haspopup="true"
          aria-label={isInAnyDeck ? 'In deck - click to manage' : 'Add to deck'}
          title={isInAnyDeck ? 'In deck' : 'Add to deck'}
        >
          <GiCardPlay className="h-5 w-5" />
        </button>
      ) : (
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={clsx(
            'flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm transition-colors',
            isInAnyDeck ? 'text-aether hover:bg-aether/10' : 'text-silver hover:text-pearl hover:bg-slate',
            className,
          )}
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          <GiCardPlay className="h-4 w-4" />
          Deck
        </button>
      )}

      {/* Dropdown menu */}
      {isOpen && (
        <div className="border-storm bg-obsidian shadow-void/50 absolute top-full right-0 z-50 mt-2 w-64 rounded-lg border py-1 shadow-xl">
          {/* Header */}
          <div className="border-storm border-b px-3 py-2">
            <span className="text-silver text-xs font-medium tracking-wide uppercase">Add to Deck</span>
          </div>

          {/* Decks list */}
          <div className="max-h-64 overflow-y-auto py-1">
            {availableDecks.length === 0 ? (
              <p className="text-ash px-4 py-3 text-center text-sm">No decks yet</p>
            ) : (
              availableDecks.map((deck) => <DeckRow key={deck.id} deck={deck} card={card} onAdd={handleAddToDeck} isLoading={isLoading} />)
            )}
          </div>

          {/* Hint footer */}
          <div className="border-storm border-t px-3 py-2">
            <p className="text-ash text-xs">Click to add to main deck. Hover for zone options.</p>
          </div>
        </div>
      )}
    </div>
  );
}
